import React, { useState, useEffect, useMemo } from 'react';
import { RoomAccessRule, RoomData, Wing, Player } from '../types';
import { db, doc, setDoc, safeOnSnapshot, collection, getDocs, updateDoc, functions, usFunctions, httpsCallable, deleteDoc } from '../firebaseConfig';
import { AVATARS } from '../constants';
import { isStudent } from '../utils/roles';
import { logSisterAction } from '../sisterAudit';

interface GuardianMirrorProps {
  onClose: () => void;
  wings: Wing[];
  rooms: Record<string, RoomData>;
  onUpdateWing: (id: string, updates: Partial<Wing>) => Promise<void>;
  onCreateWing: (id: string, name: string) => Promise<void>;
  onDeleteWing: (id: string) => Promise<void>;
  onUpdateRoom: (id: string, updates: Partial<RoomData>) => Promise<void>;
  hubBackgroundUrl?: string;
  onUpdateHub?: (url: string) => Promise<void>;
  onCreateRoom: (id: string, name: string) => Promise<void>;
  onDeleteRoom: (id: string) => Promise<void>;
  setGuideMessage: (msg: string) => void;
  currentUser: Player;
}

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') {
    return val.da || val.en || val.nb || Object.values(val).find(v => typeof v === 'string') || "";
  }
  return String(val);
};

const ACADEMY_RANKS = [
  { id: 'hatchlings',   label: 'Hatchlings (0. kl.)' },
  { id: 'sparks',       label: 'Sparks (1. kl.)' },
  { id: 'embers',       label: 'Embers (2. kl.)' },
  { id: 'fledglings',   label: 'Fledglings (3. kl.)' },
  { id: 'drakes',       label: 'Drakes (4. kl.)' },
  { id: 'seekers',      label: 'Seekers (5. kl.)' },
  { id: 'sentinels',    label: 'Sentinels (6. kl.)' },
  { id: 'wyverns',      label: 'Wyverns (7. kl.)' },
  { id: 'stormriders',  label: 'Stormriders (8. kl.)' },
  { id: 'flamebearers', label: 'Flamebearers (9. kl.)' },
  { id: 'ascended',     label: 'Ascended (10. kl.)' },
  { id: 'vanguards',    label: 'Vanguards (HF/Gym 1)' },
  { id: 'mystics',      label: 'Mystics (HF/Gym 2)' },
  { id: 'elders',       label: 'Elders (HF/Gym 3)' },
  { id: 'parents',      label: 'Forældre' },
  { id: 'warden',       label: 'Wardens (Guardians)' },
];

const compressImage = (base64Str: string, maxWidth = 1920, maxHeight = 1080): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > height) {
        if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
      } else {
        if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
  });
};




const GuardianMirror: React.FC<GuardianMirrorProps> = ({ currentUser, onClose, wings, rooms, onUpdateWing, onCreateWing, onDeleteWing, onUpdateRoom, onCreateRoom, onDeleteRoom, hubBackgroundUrl, onUpdateHub }) => {
  const [selectedId, setSelectedId] = useState<string>('');

  const moveWingUp = async (wingId: string) => {
    const sortedWings = [...wings].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    const wingIndex = sortedWings.findIndex(w => w.id === wingId);
    if (wingIndex > 0) {
      const currentWing = sortedWings[wingIndex];
      const previousWing = sortedWings[wingIndex - 1];
      const reordered = [...sortedWings];
      [reordered[wingIndex], reordered[wingIndex - 1]] = [reordered[wingIndex - 1], reordered[wingIndex]];
      await Promise.all(reordered.map((w, i) => w.displayOrder === i ? Promise.resolve() : onUpdateWing(w.id, { displayOrder: i })));
    }
  };

  const moveWingDown = async (wingId: string) => {
    const sortedWings = [...wings].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    const wingIndex = sortedWings.findIndex(w => w.id === wingId);
    if (wingIndex < wings.length - 1) {
      const currentWing = sortedWings[wingIndex];
      const nextWing = sortedWings[wingIndex + 1];
      const reordered = [...sortedWings];
      [reordered[wingIndex], reordered[wingIndex + 1]] = [reordered[wingIndex + 1], reordered[wingIndex]];
      await Promise.all(reordered.map((w, i) => w.displayOrder === i ? Promise.resolve() : onUpdateWing(w.id, { displayOrder: i })));
    }
  };

  const moveRoomUp = async (roomId: string) => {
    const sortedRooms = Object.values(rooms).filter(r => r.id !== 'config_global').sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    const roomIndex = sortedRooms.findIndex(r => r.id === roomId);
    if (roomIndex > 0) {
      const currentRoom = sortedRooms[roomIndex];
      const previousRoom = sortedRooms[roomIndex - 1];
      const reordered = [...sortedRooms];
      [reordered[roomIndex], reordered[roomIndex - 1]] = [reordered[roomIndex - 1], reordered[roomIndex]];
      await Promise.all(reordered.map((r, i) => r.displayOrder === i ? Promise.resolve() : onUpdateRoom(r.id, { displayOrder: i })));
    }
  };

  const moveRoomDown = async (roomId: string) => {
    const sortedRooms = Object.values(rooms).filter(r => r.id !== 'config_global').sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    const roomIndex = sortedRooms.findIndex(r => r.id === roomId);
    if (roomIndex < sortedRooms.length - 1) {
      const currentRoom = sortedRooms[roomIndex];
      const nextRoom = sortedRooms[roomIndex + 1];
      const reordered = [...sortedRooms];
      [reordered[roomIndex], reordered[roomIndex + 1]] = [reordered[roomIndex + 1], reordered[roomIndex]];
      await Promise.all(reordered.map((r, i) => r.displayOrder === i ? Promise.resolve() : onUpdateRoom(r.id, { displayOrder: i })));
    }
  };
  const [selectionType, setSelectionType] = useState<'room' | 'wing' | 'user' | 'hub'>('wing');
  const [activeTab, setActiveTab] = useState<'design' | 'rules' | 'users' | 'create'>('design');
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [aiPreview, setAiPreview] = useState<{targetId: string, targetType: 'room' | 'wing', field: string, url: string} | null>(null);
  
  const [rules, setRules] = useState<Record<string, RoomAccessRule>>({});
  const [allUsers, setAllUsers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [accessFilter, setAccessFilter] = useState<'' | 'with' | 'without'>('');
  const [userAccessSearch, setUserAccessSearch] = useState('');
  const [availableGuilds, setAvailableGuilds] = useState<{id: string, name: string}[]>([]);
  const [availableClassrooms, setAvailableClassrooms] = useState<{id: string; name: string}[]>([]);

  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [createType, setCreateType] = useState<'room' | 'wing'>('wing');

  // SLETTE-LOGIK STATE
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const uSnap = await getDocs(collection(db, "users"));
      const usersData = uSnap.docs.map(doc => {
        const d = doc.data() as any;
        return {
          id: doc.id,
          ...d,
          wizardName: s(d.wizardName || d.wizard_name || d.username || d.displayName || "Unavngiven"),
          realName: s(d.realName || d.real_name || d.full_name || d.name || ""),
          avatarUrl: d.avatarUrl || d.customAvatar || d.photoURL || AVATARS[0],
        } as Player;
      });
      setAllUsers(usersData);

      const gSnap = await getDocs(collection(db, "guilds"));
      setAvailableGuilds(gSnap.docs.map(d => ({ id: d.id, name: s(d.data().name) })));
      const cSnap = await getDocs(collection(db, "classrooms"));
      setAvailableClassrooms(cSnap.docs.map(d => ({ id: d.id, name: s(d.data().name) })));
    };
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    const unsub = safeOnSnapshot(collection(db, "room_rules"), (snap: any) => {
      const r: Record<string, RoomAccessRule> = {};
      snap.forEach((d: any) => { 
        r[d.id] = { id: d.id, ...d.data() } as RoomAccessRule; 
      });
      setRules(r);
    });
    return () => unsub();
  }, []);

  const [wingRulesLocal, setWingRulesLocal] = useState<Record<string, any>>({});
  useEffect(() => {
    const unsub = safeOnSnapshot(collection(db, "wing_rules"), (snap: any) => {
      const r: Record<string, any> = {};
      snap.forEach((d: any) => { 
        r[d.id] = { id: d.id, ...d.data() }; 
      });
      setWingRulesLocal(r);
    });
    return () => unsub();
  }, []);

  const filteredUsers = useMemo(() => {
    const low = searchTerm.toLowerCase();
    return allUsers.filter(u => 
      (u.wizardName || '').toLowerCase().includes(low) ||
      (u.realName || '').toLowerCase().includes(low) ||
      (u.email || '').toLowerCase().includes(low) ||
      (u.name || '').toLowerCase().includes(low)
    );
  }, [allUsers, searchTerm]);

  const currentItem = selectionType === 'room' ? rooms[selectedId] : selectionType === 'user' ? allUsers.find(u => u.id === selectedId) : wings.find(w => w.id === selectedId);

  const currentRule: RoomAccessRule = useMemo(() => {
    const r = selectionType === 'wing' ? wingRulesLocal[selectedId] : rules[selectedId];
    return { 
      id: selectedId, 
      roomId: selectedId, 
      allowedAges: r?.allowedAges || [], 
      allowedGuilds: r?.allowedGuilds || [], 
      allowedFellowships: r?.allowedFellowships || [], 
      allowedClassrooms: r?.allowedClassrooms || [], 
      allowedSubjects: r?.allowedSubjects || [], 
      allowedUsers: r?.allowedUsers || [], 
      logic: r?.logic || 'OR', 
      isActive: r?.isActive ?? true
    };
  }, [rules, wingRulesLocal, selectedId, selectionType]);

  const handleUpdateRule = async (updates: Partial<RoomAccessRule>) => {
    const collectionName = selectionType === 'wing' ? "wing_rules" : "room_rules";
    await setDoc(doc(db, collectionName, selectedId), { ...currentRule, ...updates }, { merge: true }); logSisterAction(currentUser, 'campus', 'KONTROL', 'OPDATERET_VOGTERREGEL', selectedId, '');
  };

  const handleUserAccessToggle = async (userId: string) => {
    const isAllowed = currentRule.allowedUsers.includes(userId);
    await handleUpdateRule({
      allowedUsers: isAllowed 
        ? currentRule.allowedUsers.filter(id => id !== userId) 
        : [...currentRule.allowedUsers, userId]
    });
  };

  const handleGenerateImage = async (field: string) => {
    if (!currentItem) return;
    setIsGenerating(field);
    try {
      // RETTET:
const callGemini = httpsCallable(usFunctions, 'cp_callGeminiVision');
const promptText = `Fantasy Norewyn Academy style, ${field.includes('Background') || field.includes('background') ? 'interior environment' : 'magical portal gateway'}, ${s((currentItem as any).name)}, cinematic lighting, intricate details, high fantasy, ethereal`;

const aspectRatio = (field.includes('Background') || field.includes('background')) ? "16:9" : "3:4";

const result: any = await callGemini({
  task: 'generateImage',
  payload: {
    prompt: promptText,
    aspectRatio: aspectRatio
  }
});

      if (result.data) {
        const compressed = await compressImage(`data:image/png;base64,${result.data}`);
        if (selectionType === 'hub' && onUpdateHub) {
      await onUpdateHub(compressed);
    } else {
      setAiPreview({ targetId: selectedId, targetType: selectionType as any, field, url: compressed });
    }
      } else {
        throw new Error("Ingen billed-data modtaget fra portalen.");
      }
    } catch (err) {
      console.error("AI Portal Fejl:", err);
      alert("Energien flimrer... Portalen kunne ikke skabe billedet lige nu.");
    } finally { 
      setIsGenerating(null); 
    }
  };

  const isCurrentActive = currentItem && (currentItem as any).isActive !== false;

  const toggleItemVisibility = async () => {
    if (!selectedId || !selectionType) return;
    const nextState = !isCurrentActive;
    if (selectionType === 'hub') {
      if (onUpdateHub) await onUpdateHub(aiPreview?.url || '');
      return;
    } else if (selectionType === 'wing') {
      await onUpdateWing(selectedId, { isActive: nextState });
    } else if (selectionType === 'room') {
      await onUpdateRoom(selectedId, { isActive: nextState });
    }
  };

  // --- NY FUNKTION: HÅRD SLETNING ---
  const handleHardDelete = async () => {
    if (!selectedId || !selectionType) return;
    
    const confirmName = prompt(`ADVARSEL: Dette sletter "${s((currentItem as any).name)}" PERMANENT fra alle arkiver.\n\nSkriv dør/sektor navnet herunder for at bekræfte:`);
    
    if (confirmName === s((currentItem as any).name)) {
      try {
        if (selectionType === 'wing') {
          await onDeleteWing(selectedId);
          await deleteDoc(doc(db, "wing_rules", selectedId)).catch(() => {}); logSisterAction(currentUser, 'campus', 'KONTROL', 'SLETTET_VINGEREGEL', selectedId, '');
        } else if (selectionType === 'room') {
          await onDeleteRoom(selectedId);
          // Slet også reglerne tilknyttet rummet
          await deleteDoc(doc(db, "room_rules", selectedId)); logSisterAction(currentUser, 'campus', 'KONTROL', 'SLETTET_RUMREGEL', selectedId, '');
        }
        setSelectedId('');
        setIsDeleting(false);
        alert("Manifesteringen er opløst. Objektet findes ikke længere.");
      } catch (err) {
        console.error("Slette fejl:", err);
        alert("Noget blokerede for destruktionen.");
      }
    } else {
      alert("Navnet matchede ikke. Destruktion afbrudt.");
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/95 backdrop-blur-2xl overflow-hidden p-0 md:p-6">
      <div className="bg-[#0a0a1a] border-y md:border-2 border-yellow-500/30 w-full h-full md:rounded-[3rem] overflow-hidden flex flex-col shadow-2xl relative">
        
        {/* AI PREVIEW MODAL */}
        {aiPreview && (
          <div className="absolute inset-0 z-[500] bg-black/98 flex flex-col items-center justify-center p-6 animate-fade-in">
             <div className="max-w-4xl w-full text-center space-y-8">
                <h3 className="text-3xl font-cinzel text-yellow-500 uppercase tracking-widest">Nyt Magisk Motiv</h3>
                <div className="aspect-video max-h-[60vh] mx-auto rounded-3xl overflow-hidden border-2 border-yellow-500/50 shadow-[0_0_50px_rgba(234,179,8,0.3)]">
                  <img src={aiPreview.url} className="w-full h-full object-cover" alt="AI Preview" />
                </div>
                <div className="flex justify-center gap-6">
                   <button onClick={async () => {
                     if (aiPreview.targetType === 'wing') await onUpdateWing(aiPreview.targetId, { [aiPreview.field]: aiPreview.url });
                     else await onUpdateRoom(aiPreview.targetId, { [aiPreview.field]: aiPreview.url });
                     setAiPreview(null);
                   }} className="bg-yellow-500 hover:bg-yellow-400 text-black px-12 py-4 rounded-2xl font-black uppercase text-sm transition-all shadow-xl active:scale-95">Manifester</button>
                   <button onClick={() => setAiPreview(null)} className="bg-white/10 hover:bg-white/20 text-white px-12 py-4 rounded-2xl font-black uppercase text-sm transition-all border border-white/10">Omlav</button>
                </div>
             </div>
          </div>
        )}

        {/* HEADER */}
        <div className="p-5 md:p-8 border-b border-white/10 flex justify-between items-center bg-indigo-900/30 shrink-0">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="w-10 h-10 md:w-16 md:h-16 bg-yellow-500/10 rounded-2xl flex items-center justify-center border border-yellow-500/30">
               <span className="text-2xl md:text-4xl">🪞</span>
            </div>
            <div>
              <h2 className="text-lg md:text-3xl font-cinzel text-yellow-500 tracking-widest uppercase leading-none mb-1">Vogter-Spejlet</h2>
              <p className="text-[8px] md:text-[10px] font-black uppercase text-indigo-400 tracking-widest opacity-60">Kontrolcenter & Arkiv</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-4xl md:text-5xl transition-colors">&times;</button>
        </div>

        <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
          {/* SIDEBAR */}
          <div className="w-full md:w-80 border-b md:border-r border-white/5 bg-black/40 flex flex-col shrink-0 min-h-0">
            <div className="p-4 grid grid-cols-2 gap-2 shrink-0">
              <button onClick={() => { setActiveTab('create'); setSelectedId(''); }} className="bg-indigo-600 hover:bg-indigo-500 text-white p-3 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all shadow-lg">+ NY</button>
              <button onClick={() => { setActiveTab('users'); setSelectedId(''); }} className={`p-3 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all ${activeTab === 'users' ? 'bg-yellow-500 text-black' : 'bg-white/5 text-white hover:bg-white/10'}`}>👥 BRUGERE</button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-8 no-scrollbar pb-32">
              <section className="space-y-3">
                <h4 className="text-[9px] font-black text-yellow-500 uppercase tracking-widest border-l-2 border-yellow-500 pl-3">Forhallen</h4>
                <button onClick={() => { setSelectedId('hub'); setSelectionType('hub'); setActiveTab('design'); }} className={`w-full text-left p-4 rounded-xl text-[11px] font-bold border transition-all flex items-center gap-3 ${selectedId === 'hub' && selectionType === 'hub' ? 'bg-yellow-500 text-black border-yellow-400 shadow-lg' : 'bg-white/5 border-transparent text-indigo-200 hover:bg-white/10'}`}>
                  <span>🏰</span> Forhallens baggrund
                </button>
              </section>
              <section className="space-y-3">
                <h4 className="text-[9px] font-black text-indigo-500 uppercase tracking-widest border-l-2 border-indigo-500 pl-3">Sektorer (Wings)</h4>
                <div className="space-y-2">
                  {[...wings].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)).map(w => (
                    <button key={w.id} onClick={() => { setSelectedId(w.id); setSelectionType('wing'); setActiveTab('design'); }} className={`w-full text-left p-4 rounded-xl text-[11px] font-bold border transition-all truncate flex items-center gap-3 ${selectedId === w.id && selectionType === 'wing' ? 'bg-yellow-500 text-black border-yellow-400 shadow-lg' : 'bg-white/5 border-transparent text-indigo-200 hover:bg-white/10'}`}>
                      <span className="opacity-50">{w.isActive === false ? '🔒' : '🏰'}</span> {s(w.name)}<span className="ml-auto inline-flex gap-2 shrink-0"><span role="button" onClick={(e) => { e.stopPropagation(); moveWingUp(w.id); }} className="cursor-pointer text-base px-1 hover:text-yellow-300 select-none">↑</span><span role="button" onClick={(e) => { e.stopPropagation(); moveWingDown(w.id); }} className="cursor-pointer text-base px-1 hover:text-yellow-300 select-none">↓</span></span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="space-y-3">
                <h4 className="text-[9px] font-black text-indigo-500 uppercase tracking-widest border-l-2 border-indigo-500 pl-3">Døre (Rooms)</h4>
                <div className="space-y-2">
                  {Object.values(rooms).filter(r => r.id !== 'config_global').sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)).map(r => (
                    <button key={r.id} onClick={() => { setSelectedId(r.id); setSelectionType('room'); setActiveTab('design'); }} className={`w-full text-left p-4 rounded-xl text-[11px] font-bold border transition-all truncate flex items-center gap-3 ${selectedId === r.id && selectionType === 'room' ? 'bg-indigo-600 text-white border-indigo-400 shadow-lg' : 'bg-white/5 border-transparent text-indigo-200 hover:bg-white/10'}`}>
                      <span className="opacity-50">{r.isActive === false ? '🔒' : r.id === 'HALLWAY' ? '🏛️' : '🔮'}</span>
                      <span className="flex-1 truncate">{s(r.name)}</span>
                      {r.id === 'HALLWAY' && <span className="ml-1 text-[7px] bg-yellow-500/20 text-yellow-500 px-1 rounded font-black uppercase">Forhal</span>}
                      {(r.wingIds?.length || 0) > 1 && <span className="ml-1 text-[7px] bg-indigo-500/20 text-indigo-300 px-1 rounded font-black uppercase">{r.wingIds.length} wings</span>}
                      <span className="ml-auto inline-flex gap-2 shrink-0"><span role="button" onClick={(e) => { e.stopPropagation(); moveRoomUp(r.id); }} className="cursor-pointer text-base px-1 hover:text-yellow-300 select-none">↑</span><span role="button" onClick={(e) => { e.stopPropagation(); moveRoomDown(r.id); }} className="cursor-pointer text-base px-1 hover:text-yellow-300 select-none">↓</span></span>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="flex-1 flex flex-col bg-black/20 min-h-0">
            {(selectedId || activeTab === 'create' || activeTab === 'users') ? (
              <>
                <div className="flex px-4 md:px-12 border-b border-white/5 bg-indigo-950/20 shrink-0">
                  <button onClick={() => setActiveTab('design')} className={`px-8 py-5 text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] border-b-2 transition-all ${activeTab === 'design' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-indigo-400 hover:text-white'}`}>Design</button>
                  <button onClick={() => setActiveTab('rules')} className={`px-8 py-5 text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] border-b-2 transition-all ${activeTab === 'rules' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-indigo-400 hover:text-white'}`}>Adgang</button>
                  <button onClick={() => setActiveTab('users')} className={`px-8 py-5 text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] border-b-2 transition-all ${activeTab === 'users' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-indigo-400 hover:text-white'}`}>Elever</button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-12 no-scrollbar pb-32">
                  {activeTab === 'design' && currentItem && (
                    <div className="max-w-4xl mx-auto space-y-12 animate-fade-in">
                      <div className="flex flex-col md:flex-row gap-8 items-start">
                        <div className="flex-1 space-y-4 w-full">
                          <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Vist Navn</label>
                          <input type="text" value={s((currentItem as any).name)} onChange={(e) => selectionType === 'wing' ? onUpdateWing(selectedId, { name: e.target.value }) : onUpdateRoom(selectedId, { name: e.target.value })} className="w-full bg-white/5 border border-white/10 p-6 rounded-2xl text-white outline-none focus:border-yellow-500/50 text-xl font-cinzel transition-all shadow-inner" />
                        </div>
                        
                        <div className="flex flex-col gap-4">
                           {/* SYNLIGHEDSKONTROL */}
                           <div className="bg-indigo-900/20 p-6 rounded-[2rem] border border-white/5 flex items-center gap-6">
                              <div className="flex flex-col">
                                <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1">Synlighedssegl</label>
                                <span className={`text-[11px] font-black uppercase tracking-widest ${isCurrentActive ? 'text-green-400' : 'text-red-500'}`}>
                                  {isCurrentActive ? 'Aktiv' : 'Skjult'}
                                </span>
                              </div>
                              <button onClick={toggleItemVisibility} className={`w-14 h-7 rounded-full relative transition-all shadow-lg ${isCurrentActive ? 'bg-green-600' : 'bg-red-600'}`}>
                                 <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all shadow-sm ${isCurrentActive ? 'left-8' : 'left-1'}`} />
                              </button>
                           </div>

                           {/* --- SLETTE KNAP --- */}
                           <button 
                            onClick={handleHardDelete}
                            className="p-3 rounded-2xl bg-red-950/20 border border-red-500/20 text-red-500 text-[9px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all text-center"
                            >
                        🗑️ Slet Permanent
                        </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                         <div className="space-y-8">
                            <div className="space-y-3">
                              <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Lore & Beskrivelse</label>
                              <textarea 
                                value={s((currentItem as any).description)} 
                                onChange={(e) => selectionType === 'wing' ? onUpdateWing(selectedId, { description: e.target.value }) : onUpdateRoom(selectedId, { description: e.target.value })} 
                                className="w-full bg-white/5 border border-white/10 p-5 rounded-2xl text-white outline-none focus:border-yellow-500/50 text-sm italic transition-all h-32 resize-none shadow-inner"
                                placeholder="Skriv de mystiske detaljer om dette sted..."
                              />
                            </div>

                            {(selectionType === 'wing' || selectedId !== 'HALLWAY') && (
                              <div className="space-y-3">
                                <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Hvis eleven ikke har adgang</label>
                                <div className="flex flex-wrap gap-2">
                                  {[{ k: 'lock', t: 'Vis med l\u00e5s' }, { k: 'dim', t: 'Vis nedtonet' }, { k: 'hide', t: 'Skjul helt' }].map(o => (
                                    <button key={o.k} onClick={() => selectionType === 'wing' ? onUpdateWing(selectedId, { lockedDisplay: o.k as any }) : onUpdateRoom(selectedId, { lockedDisplay: o.k as any })} className={'px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ' + (((((currentItem as any) || {}).lockedDisplay) || 'lock') === o.k ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/40 border-white/10 text-white/30 hover:text-white/60')}>{o.t}</button>
                                  ))}
                                </div>
                                <p className="text-[9px] text-white/40 italic">Kun elever. Vogtere og admin ser altid alt.</p>
                              </div>
                            )}
                            {selectionType === 'room' && selectedId !== 'HALLWAY' && (
  <div className="space-y-3">
    <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Zoom Møde Link / ID</label>
    <input type="text" placeholder="https://zoom.us/j/..." value={(currentItem as RoomData).zoomMeetingId || ''} onChange={(e) => onUpdateRoom(selectedId, { zoomMeetingId: e.target.value })} className="w-full bg-black/60 border border-white/10 p-5 rounded-xl text-white font-mono text-xs outline-none focus:border-indigo-500 transition-all shadow-inner" />
  </div>
)}

                            {selectionType === 'room' && (
                              <div className="bg-indigo-950/20 p-8 rounded-[2rem] border border-white/5 space-y-5">
                                 <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest border-b border-indigo-500/20 pb-2">Placering i Sektorer</h5>
                                 <div className="flex flex-wrap gap-2">
                                   {wings.map(wing => (
                                     <button key={wing.id} onClick={() => {
                                        const cur = (currentItem as RoomData).wingIds || [];
                                        const next = cur.includes(wing.id) ? cur.filter(id => id !== wing.id) : [...cur, wing.id];
                                        onUpdateRoom(selectedId, { wingIds: next });
                                     }} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${ (currentItem as RoomData).wingIds?.includes(wing.id) ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/40 border-white/10 text-white/30 hover:text-white/60'}`}>
                                       🏰 {s(wing.name)}
                                     </button>
                                   ))}
                                 </div>
                              </div>
                            )}
                         </div>

                         <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                               <div className="aspect-[3/4] bg-black rounded-3xl overflow-hidden border border-white/10 relative shadow-2xl group">
                                 <img src={(selectionType === 'room' ? (currentItem as RoomData).imageUrl : (currentItem as Wing).themeUrl) || ''} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-all duration-700" alt="Ikon" />
                                 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                                 <div className="absolute bottom-4 left-0 right-0 text-center text-[8px] font-black uppercase text-white/40 tracking-widest">Dør-ikon</div>
                               </div>
                               <button onClick={() => handleGenerateImage(selectionType === 'room' ? 'imageUrl' : 'themeUrl')} disabled={!!isGenerating} className="w-full bg-indigo-600 hover:bg-indigo-500 py-4 rounded-xl font-black text-[9px] uppercase border border-indigo-400/30 text-white shadow-lg transition-all active:scale-95 disabled:opacity-50">
                                 {isGenerating === (selectionType === 'room' ? 'imageUrl' : 'themeUrl') ? 'Smeder...' : '✨ Skab Ikon'}
                               </button>
                            </div>

                            <div className="space-y-4">
                               <div className="aspect-[3/4] bg-black rounded-3xl overflow-hidden border border-white/10 relative shadow-2xl group">
                                 <img src={(selectionType === 'hub' ? hubBackgroundUrl : selectionType === 'room' ? (currentItem as RoomData).roomBackgroundUrl : (currentItem as Wing).backgroundUrl) || ''} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-all duration-700" alt="Miljø" />
                                 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                                 <div className="absolute bottom-4 left-0 right-0 text-center text-[8px] font-black uppercase text-white/40 tracking-widest">Miljø</div>
                               </div>
                               <button onClick={() => handleGenerateImage(selectionType === 'hub' ? 'hubBackgroundUrl' : selectionType === 'room' ? 'roomBackgroundUrl' : 'backgroundUrl')} disabled={!!isGenerating} className="w-full bg-indigo-600 hover:bg-indigo-500 py-4 rounded-xl font-black text-[9px] uppercase border border-indigo-400/30 text-white shadow-lg transition-all active:scale-95 disabled:opacity-50">
                                 {isGenerating === (selectionType === 'hub' ? 'hubBackgroundUrl' : selectionType === 'room' ? 'roomBackgroundUrl' : 'backgroundUrl') ? 'Smeder...' : '✨ Skab Miljø'}
                               </button>
                               <label className="w-full bg-white/5 hover:bg-white/10 py-4 rounded-xl font-black text-[9px] uppercase border border-white/10 text-indigo-200 shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2">📁 Upload billede<input type="file" accept="image/*" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; const reader = new FileReader(); reader.onload = async (ev) => { const compressed = await compressImage(ev.target?.result as string); const field = selectionType === 'hub' ? 'hubBackgroundUrl' : selectionType === 'room' ? 'roomBackgroundUrl' : 'backgroundUrl'; if (selectionType === 'hub' && onUpdateHub) { await onUpdateHub(compressed); } else { setAiPreview({ targetId: selectedId, targetType: selectionType as any, field, url: compressed }); } }; reader.readAsDataURL(file); }} /></label>
                               <p className="text-[8px] text-indigo-400/50 text-center leading-relaxed">Anbefalet: 1920x1080px (16:9) · JPG/PNG</p>
                            </div>
                         </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'rules' && (
                    <div className="max-w-4xl mx-auto space-y-10 animate-fade-in pb-32">
                       <div className="bg-indigo-950/40 border border-indigo-500/20 p-8 rounded-[2.5rem] space-y-4 shadow-xl">
                          <h4 className="text-base font-cinzel text-indigo-300 uppercase tracking-widest">Sådan virker adgang</h4>
                          <div className="space-y-3 text-[12px] text-white/75 leading-relaxed">
                            <p>Adgang til et rum bygges i to lag, der kombineres:</p>
                            <p><span className="text-yellow-400 font-bold">Sektor-arv</span> — alle rum arver deres sektors adgangsregler. Ingen sektor-regler = åben for alle.</p>
                            <p><span className="text-yellow-400 font-bold">Rum-regler</span> — kan udvide adgangen (via <em>Specifikke Elever</em>) eller indsnævre den (via reglerne her på fanen).</p>
                            <p><span className="text-red-400 font-bold">Forseglede</span> sektorer eller rum lukkes for alle elever — vogtere kan stadig komme ind. Forseglingen sættes på Design-fanen.</p>
                          </div>
                       </div>
                       <div className="bg-indigo-900/20 border border-white/10 p-8 rounded-[2.5rem] space-y-6 shadow-2xl">
                          <h4 className="text-xl font-cinzel text-indigo-400 uppercase tracking-widest">Adgangs-Logik</h4>
                          <div className="flex gap-4">
                             <button onClick={() => handleUpdateRule({ logic: 'OR' })} className={`flex-1 p-5 rounded-2xl border transition-all text-[10px] font-black uppercase tracking-widest ${currentRule.logic === 'OR' ? 'bg-yellow-500 text-black border-yellow-400 shadow-lg' : 'bg-white/5 border-white/10 text-white/40'}`}>ELLER (Én betingelse er nok)</button>
                             <button onClick={() => handleUpdateRule({ logic: 'AND' })} className={`flex-1 p-5 rounded-2xl border transition-all text-[10px] font-black uppercase tracking-widest ${currentRule.logic === 'AND' ? 'bg-indigo-600 text-white border-indigo-400 shadow-lg' : 'bg-white/5 border-white/10 text-white/40'}`}>OG (Alle betingelser skal opfyldes)</button>
                          </div>
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
                            <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Tilladte Rækker</h5>
                            <p className="text-[11px] text-white/65 leading-relaxed">Klassetrin der har adgang. Bruges når lektionen er for et helt årgang på tværs af klasser.</p>
                            <div className="flex flex-wrap gap-2">
                               {ACADEMY_RANKS.map(rank => (
                                 <button key={rank.id} onClick={() => handleUpdateRule({ allowedAges: currentRule.allowedAges.includes(rank.id) ? currentRule.allowedAges.filter(a => a !== rank.id) : [...currentRule.allowedAges, rank.id] })} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${currentRule.allowedAges.includes(rank.id) ? 'bg-yellow-500 text-black border-yellow-400' : 'bg-white/5 border-white/10 text-white/40'}`}>{rank.label}</button>
                               ))}
                            </div>
                         </section>

                         <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
                            <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Tilladte Gilder</h5>
                            <p className="text-[11px] text-white/65 leading-relaxed">Gilder hvis medlemmer har adgang. Bruges til klubber, hold eller fællesskaber på tværs af klasser.</p>
                            <div className="flex flex-wrap gap-2">
                               {availableGuilds.map(guild => (
                                 <button key={guild.id} onClick={() => handleUpdateRule({ allowedGuilds: currentRule.allowedGuilds.includes(guild.id) ? currentRule.allowedGuilds.filter(g => g !== guild.id) : [...currentRule.allowedGuilds, guild.id] })} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${currentRule.allowedGuilds.includes(guild.id) ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white/5 border-white/10 text-white/40'}`}>{guild.name}</button>
                               ))}
                            </div>
                         </section>
                         <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
                            <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Tilladte Klasser</h5>
                            <p className="text-[11px] text-white/65 leading-relaxed">Elever i disse klasser får adgang gennem rum-reglen. Tomt felt = ingen begrænsning på klasse.</p>
                            <div className="flex flex-wrap gap-2">
                               {availableClassrooms.length === 0 && <p className="text-[11px] text-white/50 italic">Ingen Halls-klasser fundet</p>}
                               {availableClassrooms.map(cls => {
                                 const isAllowed = (currentRule.allowedClassrooms || []).includes(cls.id);
                                 return (
                                   <button key={cls.id} onClick={() => handleUpdateRule({ allowedClassrooms: isAllowed ? (currentRule.allowedClassrooms || []).filter(c => c !== cls.id) : [...(currentRule.allowedClassrooms || []), cls.id] })} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${isAllowed ? 'bg-yellow-500 text-black border-yellow-400' : 'bg-white/5 border-white/10 text-white/40'}`}>
                                     {cls.name || cls.id}
                                   </button>
                                 );
                               })}
                            </div>
                         </section>
                       </div>

                       <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    {selectionType === 'room' && (
    <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
        <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Tilknyttede Halls-klasser</h5>
        <p className="text-[11px] text-white/65 leading-relaxed">Vælg hvilke Halls-klasser dette rum henter elever fra. Det er medlemskab — ikke adgang i sig selv. Elever fra en tilknyttet klasse vises kun i rumlisten hvis de også opfylder adgangsreglerne ovenfor.</p>
        <div className="flex flex-wrap gap-2">
            {availableClassrooms.length === 0 && <p className="text-[11px] text-white/50 italic">Ingen Halls-klasser fundet</p>}
            {availableClassrooms.map(cls => {
                const currentIds: string[] = rooms[selectedId]?.classroomIds || (rooms[selectedId]?.classroomId ? [rooms[selectedId].classroomId] : []);
                const isSelected = currentIds.includes(cls.id);
                return (
                  <button key={cls.id} onClick={async () => {
                    const next = isSelected ? currentIds.filter(id => id !== cls.id) : [...currentIds, cls.id];
                    await updateDoc(doc(db, 'rooms', selectedId), { classroomIds: next, classroomId: next[0] || null }); logSisterAction(currentUser, 'campus', 'KONTROL', 'TILDELT_HOLD_TIL_RUM', selectedId, '');
                }} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${isSelected ? 'bg-green-600/30 border-green-500 text-green-300' : 'bg-white/5 border-white/10 text-white/40'}`}>
                        {cls.name || cls.id}
                    </button>
                );
            })}
        </div>
        {(() => {
          const ids: string[] = rooms[selectedId]?.classroomIds || (rooms[selectedId]?.classroomId ? [rooms[selectedId].classroomId] : []);
          if (ids.length === 0) return null;
          return (
            <p className="text-[11px] text-green-400 font-medium">✓ Tilknyttet: {ids.map(id => availableClassrooms.find(c => c.id === id)?.name || id).join(', ')}</p>
          );
        })()}
          </section>
          )}
      </div>

{selectionType === 'room' && (
<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
    <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
        <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Gæstevogtere i dette rum</h5>
        <p className="text-[11px] text-white/65 leading-relaxed">Vogtere der altid vises i dette rum uanset klasse-tilknytning. Bruges til vikarer, gæstelærere eller faglige eksperter.</p>
        <div className="flex flex-wrap gap-2">
            {allUsers.filter(u => u.role === 'guardian').map(u => {
                const isAdded = (rooms[selectedId]?.guestGuardianIds || []).includes(u.id);
                return (
                    <button key={u.id} onClick={async () => {
                        const cur = rooms[selectedId]?.guestGuardianIds || [];
                        const next = isAdded ? cur.filter((id: string) => id !== u.id) : [...cur, u.id];
                        await updateDoc(doc(db, 'rooms', selectedId), { guestGuardianIds: next });
                    }} className={`px-3 py-1.5 rounded-xl text-[9px] font-bold border transition-all ${isAdded ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white/5 border-white/10 text-white/40'}`}>
                        {u.wizardName}
                    </button>
                );
            })}
        </div>
    </section>

    <section className="bg-black/40 p-8 rounded-[2rem] border border-white/5 space-y-6 shadow-xl">
        <h5 className="text-[10px] font-black text-yellow-500 uppercase tracking-widest">Synlige Admins/Superadmins</h5>
        <p className="text-[9px] text-white/30">Admins der eksplicit vises som vogtere i dette rum.</p>
        <div className="flex flex-wrap gap-2">
            {allUsers.filter(u => u.role === 'admin' || u.role === 'superadmin').map(u => {
                const isAdded = (rooms[selectedId]?.visibleAdminIds || []).includes(u.id);
                return (
                    <button key={u.id} onClick={async () => {
                        const cur = rooms[selectedId]?.visibleAdminIds || [];
                        const next = isAdded ? cur.filter((id: string) => id !== u.id) : [...cur, u.id];
                        await updateDoc(doc(db, 'rooms', selectedId), { visibleAdminIds: next });
                    }} className={`px-3 py-1.5 rounded-xl text-[9px] font-bold border transition-all ${isAdded ? 'bg-yellow-600 border-yellow-400 text-black' : 'bg-white/5 border-white/10 text-white/40'}`}>
                        {u.wizardName}
                    </button>
                );
            })}
        </div>
    </section>
</div>
)}

<section className="bg-indigo-950/20 p-8 rounded-[2.5rem] border border-white/5 space-y-8 shadow-2xl">
                          <h4 className="text-xl font-cinzel text-white uppercase tracking-widest">Specifikke Elever</h4>
                          <p className="text-[11px] text-white/70 leading-relaxed">Giv eller fjern direkte adgang for enkelte elever. Status viser om eleven allerede har adgang via klasse, række, gilde eller sektor-arv — så du ikke ved et uheld dublerer.</p>
                          {(() => {
                            const students = allUsers.filter(u => isStudent(u.role));
                            const getStatus = (u: Player): { label: string; color: string; hasAccess: boolean } => {
                              if (currentRule.allowedUsers.includes(u.id)) return { label: 'Direkte adgang', color: 'text-green-400 bg-green-600/20 border-green-500', hasAccess: true };
                              const classIds = (u as any).classIds || [];
                              if (classIds.some((c: string) => (currentRule.allowedClassrooms || []).includes(c))) return { label: `Via klasse`, color: 'text-blue-300 bg-blue-600/20 border-blue-500/50', hasAccess: true };
                              if ((u as any).ageGroup && (currentRule.allowedAges || []).includes((u as any).ageGroup)) return { label: `Via række`, color: 'text-blue-300 bg-blue-600/20 border-blue-500/50', hasAccess: true };
                              if ((u as any).guild && (currentRule.allowedGuilds || []).includes((u as any).guild)) return { label: `Via gilde`, color: 'text-blue-300 bg-blue-600/20 border-blue-500/50', hasAccess: true };
                              return { label: 'Ingen adgang', color: 'text-white/30 bg-white/5 border-white/10', hasAccess: false };
                            };
                            const filtered = students
                              .filter(u => !accessFilter || (accessFilter === 'with' ? getStatus(u).hasAccess : !getStatus(u).hasAccess))
                              .filter(u => !userAccessSearch || u.wizardName?.toLowerCase().includes(userAccessSearch.toLowerCase()) || u.realName?.toLowerCase().includes(userAccessSearch.toLowerCase()))
                              .sort((a, b) => Number(getStatus(b).hasAccess) - Number(getStatus(a).hasAccess));
                            const counts = { all: students.length, withAccess: students.filter(u => getStatus(u).hasAccess).length };
                            return (
                              <>
                                <div className="flex flex-wrap gap-2">
                                  {[
                                    { id: '', label: `Alle (${counts.all})` },
                                    { id: 'with', label: `🟢 Med adgang (${counts.withAccess})` },
                                    { id: 'without', label: `🔴 Uden adgang (${counts.all - counts.withAccess})` },
                                  ].map(f => (
                                    <button key={f.id} onClick={() => setAccessFilter(f.id as any)} className={`px-4 py-2 rounded-xl text-[10px] font-bold border transition-all ${accessFilter === f.id ? 'bg-yellow-500 text-black border-yellow-400' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}>{f.label}</button>
                                  ))}
                                </div>
                                <div className="relative">
                                  <input type="text" placeholder="Filtrér listen efter navn..." value={userAccessSearch} onChange={e => setUserAccessSearch(e.target.value)} className="w-full bg-black/60 border border-white/10 p-5 rounded-2xl text-white outline-none pl-14" />
                                  <span className="absolute left-6 top-1/2 -translate-y-1/2 opacity-30">🔍</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[28rem] overflow-y-auto no-scrollbar pr-2">
                                  {filtered.length === 0 && <p className="text-[10px] text-white/30 italic col-span-full text-center py-8">Ingen elever matcher filtret.</p>}
                                  {filtered.map(user => {
                                    const status = getStatus(user);
                                    const isDirect = currentRule.allowedUsers.includes(user.id);
                                    return (
                                      <button key={user.id} onClick={() => handleUserAccessToggle(user.id)} className={`p-3 rounded-xl border flex items-center justify-between transition-all hover:bg-white/10 ${isDirect ? 'bg-green-600/20 border-green-500' : 'bg-white/5 border-white/10'}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                          <img src={user.avatarUrl || '/default-avatar.png'} className="w-8 h-8 rounded-full shrink-0 border border-white/20" alt="" />
                                          <div className="min-w-0 text-left">
                                            <p className="text-[11px] font-bold uppercase text-white truncate">{user.wizardName}</p>
                                            <p className="text-[8px] text-white/40 truncate">{((user as any).classIds || []).length > 0 ? `${((user as any).classIds || []).length} klasse(r)` : '—'}</p>
                                          </div>
                                        </div>
                                        <span className={`text-[8px] font-black uppercase px-2 py-1 rounded border shrink-0 ml-2 ${status.color}`}>{isDirect ? '✓ Direkte' : status.label}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                       </section>
                    </div>
                  )}

                  {activeTab === 'users' && (
                    <div className="max-w-5xl mx-auto space-y-8 animate-fade-in pb-32">
                       <div className="relative">
                         <span className="absolute left-6 top-1/2 -translate-y-1/2 opacity-40 text-xl">🔍</span>
                         <input type="text" placeholder="Søg efter navn eller email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-black/60 border-2 border-white/10 p-5 rounded-2xl text-white outline-none focus:border-yellow-500/30 pl-14 text-sm" />
                       </div>

                       {[
                         { label: '👑 Superadmins', color: 'border-red-500/50 bg-red-950/20', badge: 'bg-red-900 border-red-400 text-red-200', roles: ['superadmin'] },
                         { label: '🛡️ Admins', color: 'border-yellow-500/50 bg-yellow-950/20', badge: 'bg-yellow-900 border-yellow-400 text-yellow-200', roles: ['admin'] },
                         { label: '⚔️ Vogtere', color: 'border-indigo-500/50 bg-indigo-950/20', badge: 'bg-indigo-900 border-indigo-400 text-indigo-200', roles: ['guardian'] },
                         { label: '🎓 Studerende', color: 'border-white/10 bg-white/5', badge: 'bg-white/10 border-white/20 text-white/60', roles: ['student'] },
                         { label: '👁️ Observatører', color: 'border-gray-500/30 bg-gray-950/20', badge: 'bg-gray-900 border-gray-500 text-gray-300', roles: ['observer', 'parent'] },
                       ].map(group => {
                         const members = filteredUsers.filter(u => group.roles.includes(u.role as string));
                         if (members.length === 0) return null;
                         return (
                           <div key={group.label} className={`border rounded-3xl p-6 space-y-4 ${group.color}`}>
                             <div className="flex items-center gap-3 border-b border-white/10 pb-3">
                               <h4 className="font-black text-sm text-white uppercase tracking-widest">{group.label}</h4>
                               <span className="text-[9px] bg-white/10 text-white px-2 py-0.5 rounded-full font-black">{members.length}</span>
                             </div>
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                               {members.map(user => (
                                 <div key={user.id} className="bg-black/40 border border-white/10 p-4 rounded-2xl flex items-center gap-4 hover:border-white/30 transition-all">
                                   <img src={user.avatarUrl || '/default-avatar.png'} className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shrink-0" alt="" />
                                   <div className="flex-1 min-w-0">
                                     <p className="text-white font-black text-xs uppercase truncate">{user.wizardName}</p>
                                     <p className="text-white/60 text-[9px] truncate mt-0.5">{user.realName || '—'}</p>
                                     <span className={`inline-block text-[7px] font-black uppercase px-2 py-0.5 rounded border mt-1 ${group.badge}`}>{user.role}</span>
                                   </div>
                                 </div>
                               ))}
                             </div>
                           </div>
                         );
                       })}
                    </div>
                  )}

                  {activeTab === 'create' && (
                    <div className="max-w-xl mx-auto py-12 space-y-10 animate-fade-in pb-32">
                       <div className="text-center space-y-2 mb-4">
                          <h3 className="text-3xl font-cinzel text-yellow-500 uppercase tracking-widest">Skab nyt indhold</h3>
                          <p className="text-indigo-400/60 text-[10px] font-black uppercase tracking-widest">Manifestér en ny dør eller sektor i akademiet</p>
                       </div>

                       <div className="flex gap-2 p-1.5 bg-black/40 rounded-[2rem] border border-white/5 shadow-2xl">
                          <button onClick={() => setCreateType('wing')} className={`flex-1 py-4 rounded-[1.5rem] font-black uppercase text-xs tracking-widest transition-all ${createType === 'wing' ? 'bg-yellow-500 text-black shadow-lg' : 'text-indigo-400 hover:text-indigo-100'}`}>🏰 SEKTOR</button>
                          <button onClick={() => setCreateType('room')} className={`flex-1 py-4 rounded-[1.5rem] font-black uppercase text-xs tracking-widest transition-all ${createType === 'room' ? 'bg-indigo-600 text-white shadow-lg' : 'text-indigo-400 hover:text-indigo-100'}`}>🔮 DØR</button>
                       </div>
                       
                       <div className="bg-indigo-950/20 p-10 rounded-[3rem] border border-white/5 space-y-8 shadow-2xl">
                          <div className="space-y-3">
                             <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Navn på {createType === 'wing' ? 'Sektor' : 'Dør'}</label>
                             <input type="text" placeholder="F.eks. Drageriddernes Fløj..." value={newName} onChange={e => setNewName(e.target.value)} className="w-full bg-white/5 border border-white/10 p-6 rounded-2xl text-white text-xl font-cinzel outline-none focus:border-yellow-500/50 transition-all" />
                          </div>
                          <div className="space-y-3">
                             <label className="text-[10px] font-black text-indigo-400 uppercase tracking-widest ml-1">Unikt ID (Internt)</label>
                             <input type="text" placeholder="F.eks. dragefløj-1" value={newId} onChange={e => setNewId(e.target.value)} className="w-full bg-white/5 border border-white/10 p-5 rounded-2xl text-white font-mono text-sm outline-none focus:border-indigo-500/50 transition-all" />
                          </div>
                          <button onClick={async () => {
                            if (!newId || !newName) return;
                            if (createType === 'wing') await onCreateWing(newId, newName); else await onCreateRoom(newId, newName);
                            setSelectedId(newId); setSelectionType(createType); setActiveTab('design');
                            setNewId(''); setNewName('');
                          }} className={`w-full text-black py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-sm shadow-2xl transition-all active:scale-95 ${createType === 'wing' ? 'bg-yellow-500 hover:bg-yellow-400' : 'bg-indigo-500 hover:bg-indigo-400 text-white'}`}>Manifester</button>
                       </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-20 opacity-20 select-none">
                 <span className="text-9xl mb-10 animate-pulse drop-shadow-[0_0_50px_rgba(34,211,238,0.5)]">🔮</span>
                 <p className="font-cinzel text-white uppercase tracking-[0.5em] text-xl">Vælg fra Arkivet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianMirror;